import Navigation from "@/components/Navigation";
import { Card } from "@/components/ui/card";
import { Helmet } from "react-helmet";
import SchemaMarkup from "@/components/SchemaMarkup";
import Breadcrumb from "@/components/Breadcrumb";
import { createArticleSchema } from "@/utils/schemaHelpers";
import HoroscopeContent from "@/components/HoroscopeContent";

const HoroscopoTauro = () => {
  const articleSchema = createArticleSchema(
    "Tauro: Horóscopo Diario, Características y Compatibilidad con los 12 Signos",
    "Descubre el horóscopo diario, semanal y mensual de Tauro. Predicciones de amor, trabajo, salud y dinero para personas nacidas entre el 20 de abril y el 20 de mayo.",
    `${window.location.origin}/horoscopo-tauro`
  );

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Inicio",
        "item": window.location.origin
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Horóscopo Tauro",
        "item": `${window.location.origin}/horoscopo-tauro`
      }
    ]
  };

  const breadcrumbItems = [
    { name: "Inicio", url: "/" },
    { name: "Horóscopo", url: "/horoscopo" },
    { name: "Tauro" }
  ];

  return (
    <>
      <Helmet>
        <title>Tauro: Horóscopo Diario, Características y Compatibilidad con los 12 Signos</title>
        <meta name="description" content="Descubre el horóscopo diario, semanal y mensual de Tauro. Predicciones de amor, trabajo, salud y dinero para personas nacidas entre el 20 de abril y el 20 de mayo." />
        <link rel="canonical" href={`${window.location.origin}/horoscopo-tauro`} />
        <meta property="og:title" content="Tauro: Horóscopo Diario, Características y Compatibilidad" />
        <meta property="og:description" content="Descubre el horóscopo diario, semanal y mensual de Tauro. Predicciones de amor, trabajo, salud y dinero." />
        <meta property="og:url" content={`${window.location.origin}/horoscopo-tauro`} />
        <meta property="og:type" content="article" />
      </Helmet>
      <SchemaMarkup schema={articleSchema} />
      <SchemaMarkup schema={breadcrumbSchema} />
      
      <div className="min-h-screen">
        <Navigation />
        
        <main className="pt-24 pb-12 px-4">
          <div className="container mx-auto max-w-4xl">
            <Breadcrumb items={breadcrumbItems} />
            
            <div className="text-center mb-12">
              <div className="text-6xl mb-4">♉</div>
              <h1 className="text-2xl font-bold mb-4 text-glow">
                Horóscopo de Tauro
              </h1>
              <p className="text-xl text-muted-foreground">20 de Abril - 20 de Mayo</p>
            </div>

            <section className="mb-16">
              <h2 className="text-2xl font-bold mb-6 text-primary">Tu Horóscopo Tauro: Predicciones Actualizadas</h2>
              <HoroscopeContent sign="tauro" signName="Tauro" />
            </section>

            {/* Deep Characteristics */}
            <section className="mb-16">
              <h2 className="text-2xl font-bold mb-6 text-secondary">Características Profundas del Signo Tauro: El Constructor</h2>
              
              <Card className="p-8 mb-6 gradient-mystic">
                <h3 className="text-xl font-bold mb-4">Los Pilares Astrológicos de Tauro</h3>
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <h4 className="font-bold text-base mb-2 text-primary">🌍 Elemento: Tierra</h4>
                    <p className="text-muted-foreground">Enfoque en la estabilidad, lo tangible y lo sensual. Tauro está conectado con el mundo material y físico.</p>
                  </div>
                  <div>
                    <h4 className="font-bold text-base mb-2 text-secondary">🔒 Modalidad: Fijo</h4>
                    <p className="text-muted-foreground">Define la resistencia, la perseverancia y la terquedad. Una vez que toma una decisión, difícilmente cambia de opinión.</p>
                  </div>
                  <div>
                    <h4 className="font-bold text-base mb-2 text-accent">♀️ Planeta Regente: Venus</h4>
                    <p className="text-muted-foreground">Diosa del Amor, la Belleza y el Valor. Rige el placer, los sentidos y la apreciación estética.</p>
                  </div>
                </div>
              </Card>

              <Card className="p-8 mb-6 gradient-celestial">
                <h3 className="text-xl font-bold mb-4">Perfil de Personalidad Tauro: Fortalezas y Debilidades</h3>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="font-bold text-base mb-3 text-primary">✨ Fortalezas</h4>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• <strong>Lealtad:</strong> Fiel y confiable en todas sus relaciones</li>
                      <li>• <strong>Estabilidad:</strong> Proporciona seguridad y consistencia</li>
                      <li>• <strong>Sentido Práctico:</strong> Excelente para materializar ideas</li>
                      <li>• <strong>Paciencia:</strong> Sabe esperar el momento adecuado</li>
                      <li>• <strong>Sensualidad:</strong> Profunda conexión con los placeres físicos</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-bold text-base mb-3 text-secondary">⚠️ Debilidades</h4>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• <strong>Terquedad:</strong> Resistencia extrema al cambio</li>
                      <li>• <strong>Posesividad:</strong> Tendencia a aferrarse a personas y cosas</li>
                      <li>• <strong>Materialismo:</strong> Puede valorar excesivamente lo material</li>
                      <li>• <strong>Lentitud:</strong> Toma tiempo para actuar y decidir</li>
                      <li>• <strong>Indulgencia:</strong> Puede excederse en placeres</li>
                    </ul>
                  </div>
                </div>
                <div className="text-center p-4 bg-primary/10 rounded-lg">
                  <p className="text-lg font-bold text-primary">Mantra Clave: "Yo Tengo"</p>
                </div>
              </Card>

              <Card className="p-8 mb-6 gradient-mystic">
                <h3 className="text-xl font-bold mb-4">Tauro en el Amor: Sensualidad y Compromiso</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  En el amor, Tauro busca estabilidad, seguridad y una conexión física profunda. Venus, su planeta regente, lo convierte en uno de los signos más románticos y sensuales del zodiaco. Valora las demostraciones tangibles de afecto: regalos, cenas, tiempo de calidad y contacto físico.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Una vez comprometido, Tauro es extremadamente leal y devoto. Busca relaciones duraderas donde pueda construir un hogar seguro y confortable. Su naturaleza posesiva puede ser tanto una fortaleza (protección) como un desafío (celos). Necesita una pareja que valore la rutina, la comodidad y los placeres simples de la vida.
                </p>
              </Card>

              <Card className="p-8 gradient-celestial">
                <h3 className="text-xl font-bold mb-4">Tauro en el Trabajo y Finanzas: Seguridad y Excelencia Material</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Tauro brilla en carreras que requieren paciencia, perseverancia y sentido práctico. Destaca en finanzas, bienes raíces, agricultura, gastronomía, artes, música y cualquier campo que involucre belleza y calidad material. Su capacidad para trabajar consistentemente hacia objetivos a largo plazo es excepcional.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Con el dinero, Tauro es conservador y práctico. Valora la seguridad financiera y trabaja arduamente para construir patrimonio. Le gusta disfrutar de los frutos de su trabajo, pero siempre con un colchón de ahorro. Excelente para administrar recursos y hacer crecer inversiones con paciencia.
                </p>
              </Card>
            </section>

            {/* Compatibility */}
            <section>
              <h2 className="text-2xl font-bold mb-6 text-accent">Compatibilidad de Tauro con los Otros Signos Zodiacales</h2>
              
              <Card className="p-8 mb-6 gradient-mystic">
                <h3 className="text-xl font-bold mb-4">💚 Tu Match Perfecto</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-bold text-base mb-2">♍ Virgo y ♑ Capricornio</h4>
                    <p className="text-muted-foreground">
                      Los signos de tierra comparten valores fundamentales: estabilidad, practicidad y construcción a largo plazo. Con Virgo hay comprensión mutua y apoyo en los detalles. Con Capricornio, una unión poderosa basada en objetivos comunes y respeto.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-bold text-base mb-2">♋ Cáncer y ♓ Piscis</h4>
                    <p className="text-muted-foreground">
                      Los signos de agua complementan la naturaleza terrenal de Tauro. Cáncer aporta nutrición emocional y hogar, mientras que Piscis añade romance, imaginación y profundidad espiritual. Combinaciones que fluyen naturalmente.
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-8 gradient-celestial">
                <h3 className="text-xl font-bold mb-4">⚡ Los Desafíos y Lecciones de Tauro</h3>
                <div>
                  <h4 className="font-bold text-base mb-2">♏ Especialmente con Escorpio</h4>
                  <p className="text-muted-foreground mb-4">
                    La relación con Escorpio (su signo opuesto) presenta la mayor tensión creativa. Ambos son signos fijos, lo que genera batallas de voluntades épicas. Sin embargo, cuando funcionan, crean una unión de intensidad y lealtad incomparables.
                  </p>
                  <p className="text-muted-foreground">
                    Los signos de fuego (Aries, Leo, Sagitario) desafían la necesidad de estabilidad de Tauro, mientras que los signos de aire (Géminis, Libra, Acuario) pueden frustrarlo con su naturaleza cambiante. Estas relaciones enseñan a Tauro la lección crucial de la flexibilidad y el desapego.
                  </p>
                </div>
              </Card>
            </section>
          </div>
        </main>
      </div>
    </>
  );
};

export default HoroscopoTauro;
